import { useMemo } from "react";
import PropTypes from "prop-types";
import { TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioSegments } from "@/components/ui/radio-segments";
import { Stepper } from "@/components/ui/stepper";
import FieldError from "./FieldError";
import SettingsField from "./SettingsField";
import SettingsSection from "./SettingsSection";
import {
  deviceTimeZone,
  fieldId,
  MINUTES,
  UNIT_OPTIONS,
  timeZoneGroups,
  zoneLabel,
} from "./settingsForm";
import { formatNumber } from "../lib/format";

/**
 * Units, time zone and session length. Switching units says what does and
 * doesn't follow (spec §3): new sessions and the bodyweight do; past
 * sessions, seeds and the equipment block keep their own — and offers to
 * re-enter the equipment's steps in the new units.
 */
function UnitsSection({ form, settings, onChange, onReenterEquipment, errorFor }) {
  const savedUnits = settings?.units ?? "lb";
  const switched = form.units !== savedUnits;
  const equipmentBehind = form.equipment.units !== form.units;
  const savedEquipment = settings?.equipment ?? {};
  const groups = useMemo(() => timeZoneGroups(form.timezone.trim()), [form.timezone]);
  const device = deviceTimeZone();
  const zoneId = fieldId("timezone");
  const zoneHelp =
    "Where your training day starts: History's dates and “days since” count in it.";

  return (
    <SettingsSection
      id="training-settings-units"
      title="Units, time zone and session length"
      description={
        <p>
          New plans and sessions use these. Today&apos;s check-in can still change the
          length for one session.
        </p>
      }
    >
      <div className="flex flex-col gap-1.5">
        <RadioSegments
          legend="Units"
          options={UNIT_OPTIONS}
          value={form.units}
          onChange={(value) => onChange("units", value)}
        />
        <FieldError id={`${fieldId("units")}-error`}>{errorFor("units")}</FieldError>
        <p className="m-0 text-xs text-muted-foreground">
          Sessions keep the units they were logged in; switching changes what new ones use.
        </p>
      </div>

      {switched && (
        <Alert>
          <TriangleAlert aria-hidden="true" />
          <AlertTitle>New sessions will use {form.units}</AlertTitle>
          <AlertDescription>
            <p>
              Sessions you&apos;ve logged keep their units, and your bodyweight converts when you
              save. Seeds and the equipment block keep their own units too.
            </p>
            {equipmentBehind ? (
              <>
                <p>
                  The equipment still counts in {form.equipment.units}
                  {savedEquipment.dumbbell_max !== undefined &&
                    savedEquipment.units === form.equipment.units &&
                    ` (dumbbells up to ${formatNumber(savedEquipment.dumbbell_max)} ${savedEquipment.units})`}
                  . If your gym counts in {form.units}, re-enter its steps in {form.units} — they
                  aren&apos;t converted, because {form.units} plates and dumbbells step
                  differently.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-1 h-auto min-h-11 whitespace-normal md:min-h-9"
                  onClick={() => onReenterEquipment(form.units)}
                >
                  Count equipment in {form.units} and re-enter its steps
                </Button>
              </>
            ) : (
              <p>
                The equipment counts in {form.units} too — check its steps below are real{" "}
                {form.units} steps.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-1.5">
        {groups ? (
          <>
            <Label htmlFor={zoneId}>Time zone</Label>
            <NativeSelect
              id={zoneId}
              value={form.timezone}
              onChange={(event) => onChange("timezone", event.target.value)}
              aria-invalid={errorFor("timezone") ? true : undefined}
              aria-describedby={[errorFor("timezone") && `${zoneId}-error`, `${zoneId}-help`]
                .filter(Boolean)
                .join(" ")}
              className="h-11 md:h-9"
            >
              {!form.timezone && <option value="">Pick your time zone…</option>}
              {groups.map((group) => (
                <optgroup key={group.area} label={group.area}>
                  {group.zones.map((zone) => (
                    <option key={zone} value={zone}>
                      {zoneLabel(zone)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </NativeSelect>
            <FieldError id={`${zoneId}-error`}>{errorFor("timezone")}</FieldError>
            <p id={`${zoneId}-help`} className="m-0 text-xs text-muted-foreground">
              {zoneHelp}
            </p>
          </>
        ) : (
          <SettingsField
            id={zoneId}
            label="Time zone"
            value={form.timezone}
            onChange={(value) => onChange("timezone", value)}
            inputMode="text"
            placeholder="America/Denver"
            error={errorFor("timezone")}
            helper={`Type its name, like America/Denver. ${zoneHelp}`}
          />
        )}
        {device && device !== form.timezone.trim() && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-auto min-h-11 self-start whitespace-normal md:min-h-8"
            onClick={() => onChange("timezone", device)}
          >
            Use this device&apos;s zone ({zoneLabel(device)})
          </Button>
        )}
      </div>

      <Stepper
        id={fieldId("session_minutes")}
        label="Session length"
        value={form.session_minutes}
        onChange={(value) => onChange("session_minutes", value)}
        step={MINUTES.step}
        min={MINUTES.min}
        max={MINUTES.max}
        inputMode="numeric"
        unit="min"
        error={errorFor("session_minutes") || undefined}
        helper={`${MINUTES.min} to ${MINUTES.max} minutes, warm-up included. Plans fill it; over budget they cut accessories first, and under 30 minutes they suggest the Minimum dose.`}
        className="max-w-sm"
      />
    </SettingsSection>
  );
}

UnitsSection.propTypes = {
  form: PropTypes.shape({
    units: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    session_minutes: PropTypes.string.isRequired,
    equipment: PropTypes.shape({ units: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
  settings: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onReenterEquipment: PropTypes.func.isRequired,
  errorFor: PropTypes.func.isRequired,
};

export default UnitsSection;
